import {
  createContext,
  ReactNode,
  useContext,
  useState,
  useRef,
  useEffect,
} from "react";
import { createSpreadableBem } from "/Helpers/createBem";
import { XCloseIcon } from "/App.Components";
import { ColorTheme } from "/Models/Models/ColorTheme";
import useDisableScroll from "/Helpers/Hooks/useDisableScroll";
import "./ModalProvider.scss";

interface ModalContextValue {
  openModal: (children: ReactNode) => void;
  closeModal: () => void;
}

export const ModalContext = createContext<ModalContextValue | null>(null);

interface Props {
  children: ReactNode;
}

export const ModalProvider = ({ children }: Props) => {
  const [content, setContent] = useState<ReactNode | null>(null);
  const dialogRef = useRef<HTMLDialogElement>(null);

  const openModal = (children: ReactNode) => {
    setContent(children);
  };

  const closeModal = () => {
    setContent(null);
    dialogRef.current?.close();
  };

  // Disable scroll when modal is open
  useDisableScroll(!!content);

  const bem = createSpreadableBem("modal-provider");

  // Close on Escape
  useEffect(() => {
    if (!content) return;

    dialogRef.current?.showModal();
    dialogRef.current?.focus();

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeModal();
    };

    window.addEventListener("keydown", handleEscape);
    return () => window.removeEventListener("keydown", handleEscape);
  }, [content]);

  return (
    <ModalContext.Provider value={{ openModal, closeModal }}>
      {children}

      {/* Render dialog only when content exists */}
      {content && (
        <dialog
          ref={dialogRef}
          {...bem("")}
          onPointerDown={(e) => {
            if (e.target === e.currentTarget) closeModal();
          }}
        >
          <div {...bem("__centered-content")}>
            <div {...bem("__close")}>
              <button onClick={closeModal} aria-label="Close modal">
                <XCloseIcon
                  size="32"
                  thickness="Reg"
                  color={ColorTheme.iconStrong}
                />
              </button>
            </div>

            {content}
          </div>
        </dialog>
      )}
    </ModalContext.Provider>
  );
};

export const useModal = () => {
  const ctx = useContext(ModalContext);
  if (!ctx) {
    throw new Error("useModal must be used inside <ModalProvider>");
  }
  return ctx;
};

export default ModalProvider;
